import haversine from 'haversine';
import {
  createExpense,
  createReport,
  submitReport,
  updateReportApi,
  moveExpenseApi,
  deleteExpenseApi,
  getExpenseDistanceRatesApi,
  getAttendeesApi,
  updateExpenseApi,
  performExpenseAction,
  getCurrencyListApi,
  getCreditCardsListApi,
  performChargesAction,
} from '../../services/api/expense.service';
import {
  selectExpenseMiliage,
  selectExpenseMiliageLastPosition,
  selectNewReport,
} from '../selectors/expense.selector';
import { openAlert } from './alert';
import { logException } from '../../utils/logger.utils.web';
import i18n from '../../i18n';
import {
  DEFAULT_CATEGORY,
  DynamoDBFieldNames,
  ExpenseCategories,
  MileageCategories,
  TOGGLE_KEYS,
} from '../../constants/ExpenseCategories';
import {
  BILLABLE_OPTIONS,
  chargeActions,
  DEFAULT_BILLABLE_OPTION,
  EXPENSE_FORM_KEYS,
  expenseActions,
  expenseInputTypes,
} from '../../constants/Enums';
import { isNullOrEmpty, removeLostReceipt } from '../../utils/common.utils';

export const SET_EXPENSE_RESULTS = 'SET_EXPENSE_RESULTS';
export const ADD_REPORT = 'ADD_REPORT';
export const ADD_EXPENSE = 'ADD_EXPENSE';
export const SUBMIT_EXPENSE = 'SUBMIT_EXPENSE';
export const SELECT_EXPENSE = 'SELECT_EXPENSE';
export const MOVE_EXPENSE = 'MOVE_EXPENSE';
export const DELETE_EXPENSE = 'DELETE_EXPENSE';
export const UPDATE_EXPENSE = 'UPDATE_EXPENSE';
export const UPDATE_MILIAGE = 'UPDATE_MILIAGE';
export const UPDATE_LAST_POSITION = 'UPDATE_LAST_POSITION';
export const SET_EXPENSE_CATEGORIES = 'SET_EXPENSE_CATEGORIES';
export const SET_EXPENSE_DISTANCE_RATES = 'SET_EXPENSE_DISTANCE_RATES';
export const SET_EXPENSE_ATTENDEES = 'SET_EXPENSE_ATTENDEES';
export const SET_CURRENCY_LIST = 'SET_CURRENCY_LIST';
export const SET_CREDIT_CARDS_LIST = 'SET_CREDIT_CARDS_LIST';
export const SET_CURRENT_REPORT_ID_FOR_UPDATE = 'SET_CURRENT_REPORT_ID_FOR_UPDATE';
export const SET_CURRENT_SELECTED_SUBCATEGORY_FOR_MANUAL_TAB =
  'SET_CURRENT_SELECTED_SUBCATEGORY_FOR_MANUAL_TAB';
export const SET_CURRENT_RECEIPTS_OF_USER = 'SET_CURRENT_RECEIPTS_OF_USER';
export const SET_RECEIPTS_STATE = 'SET_RECEIPTS_STATE';
export const SET_CURRENT_EXPENSE_REPORT_CARD_MODAL_ITEM =
  'SET_CURRENT_EXPENSE_REPORT_CARD_MODAL_ITEM';
export const SET_LOST_RECEIPT = 'SET_LOST_RECEIPT';
export const SET_EXPENSE_VALUE = 'SET_EXPENSE_VALUE';
export const INITIATE_EXPENSE_VALUES = 'INITIATE_EXPENSE_VALUES';
export const SET_MULTIPLE_EXPENSE_VALUES = 'SET_MULTIPLE_EXPENSE_VALUES';
export const SET_SHOULD_FETCH_CHARGES = 'SET_SHOULD_FETCH_CHARGES';
export const SET_SELECTED_CHARGES_QUEUE = 'SET_SELECTED_CHARGES_QUEUE';
export const CLEAR_SELECTED_CHARGES_QUEUE = 'CLEAR_SELECTED_CHARGES_QUEUE';

export const PaymentTypes = {
  COMPANY_CARDS: 'Company Cards',
  PERSONAL_CARDS: 'Personal Cards',
  CASH: 'Cash',
};

const newExpenseReceiptsFormat = (receipts) => {
  return receipts?.map(({ receiptId }) => receiptId);
};

const getBillableType = (selectedLabel) => {
  const option = Object.values(BILLABLE_OPTIONS).find((item) => item.label === selectedLabel);
  return option ? option.value : DEFAULT_BILLABLE_OPTION.value;
};

const getToggleKeyValues = (expense, toggleKeys) => {
  return toggleKeys.reduce((acc, { key }) => {
    if (!isNullOrEmpty(expense[key])) {
      acc[key] = expense[key];
    }
    return acc;
  }, {});
};

const getSelectedSubCategoryStateNames = (formFields, stepKey) => {
  return (
    formFields?.[stepKey]?.flatMap((field) => {
      const states = [field.stateName];
      if (field.inputConfig?.inputType === expenseInputTypes.MONEY && field.currencyStateName) {
        states.push(field.currencyStateName);
      }
      return states;
    }) || []
  );
};

const assembleExpense = (expense, receipts = [], reportId, selectedSubCategory, isMileage) => {
  // expense is the redux state
  const { SubCategoryId, formFields } = selectedSubCategory;
  // Extract relevant state names from formFields (STEP_ONE and STEP_TWO)
  const selectedSubCategoryStateNames = [
    ...getSelectedSubCategoryStateNames(formFields, EXPENSE_FORM_KEYS.STEP_ONE),
    ...getSelectedSubCategoryStateNames(formFields, EXPENSE_FORM_KEYS.STEP_TWO),
  ];

  // Filter the expense object to include only allowed state names
  const filteredExpenseState = Object.keys(expense)
    .filter((key) => selectedSubCategoryStateNames.includes(key))
    .reduce((acc, key) => {
      acc[key] = expense[key];
      return acc;
    }, {});

  const toggleKeyValues = getToggleKeyValues(expense, TOGGLE_KEYS); // E.g.: Reimbursable Billable

  const newExpense = {
    ...filteredExpenseState, // Include only fields matching the selectedSubCategory formFields
    ...toggleKeyValues,
    [DynamoDBFieldNames.RECEIPTS]: newExpenseReceiptsFormat(removeLostReceipt(receipts)),
    [DynamoDBFieldNames.EXPENSE_SUB_CATEGORY]: SubCategoryId,
    [DynamoDBFieldNames.EXPENSE_CATEGORY]: getCategoryId(
      SubCategoryId,
      isMileage ? MileageCategories : ExpenseCategories
    ),
    [DynamoDBFieldNames.REPORT_ID]: reportId,
    [DynamoDBFieldNames.PAYMENT_TYPE]: getPaymentType(expense.PaymentType),
    [DynamoDBFieldNames.EXPENSE_DATE]: new Date().toLocaleDateString('en-US'),
  };
  return newExpense;
};
export const setExpenseResults = (actionType) => {
  return async (dispatch) => {
    try {
      const reportList = await performExpenseAction(actionType);
      dispatch({
        type: SET_EXPENSE_RESULTS,
        list: reportList,
      });
    } catch (error) {
      logException(error, {
        metaData: {
          error,
          message: 'Setting Expense Results Failed.',
        },
      });
      console.error('error ', error);
    }
  };
};

export const addReport = (expense) => {
  return async (dispatch, getState) => {
    const newReport = selectNewReport(getState());
    const res = await createReport({
      Name: newReport.Name,
      Expenses: [assembleExpense(expense)],
    });

    if (!res) {
      dispatch(
        openAlert({
          title: i18n.t('tripExpense:ExpenseMessage.ReportNoCreated'),
          confirmText: 'Continue',
        })
      );
    }
    const actionType = { ActionCode: expenseActions.GET_REPORTS_AND_EXPENSES };
    await dispatch(setExpenseResults(actionType));
  };
};

export const getPaymentType = (paymentType) => {
  if (paymentType?.text && paymentType?.value) {
    return paymentType.value;
  } else {
    return 'Unknown Payment Type';
  }
};

export const getPaymentTypeText = (paymentType) => {
  if (paymentType?.text && paymentType?.value) {
    return paymentType.text;
  } else {
    return 'Unknown Payment Type';
  }
};

export const getCategoryId = (subCategoryId, expenseCategories) => {
  for (const category of expenseCategories) {
    // Check each category's Categories array for matching CategoryId
    for (const subCategory of category.SubCategories) {
      if (subCategory.SubCategoryId === subCategoryId) {
        // If CategoryId matches, return groupKey or default 1
        return category.CategoryId || '1';
      }
    }
  }
};

export const addExpense = async (expense, reportId, selectedCharge, isMileage = false) => {
  return async (dispatch) => {
    const receipts = expense.receipts;
    const currentSelectedSubCategory = expense[DynamoDBFieldNames.SELECTED_SUBCATEGORY];
    const newExpense = assembleExpense(
      expense,
      receipts,
      reportId,
      currentSelectedSubCategory,
      isMileage
    );
    if (!isNullOrEmpty(selectedCharge)) {
      newExpense.Charge = selectedCharge;
    }
    const res = await createExpense(newExpense);
    if (!res) {
      dispatch(
        openAlert({
          title: i18n.t('tripExpense:ExpenseMessage.ExpenseNoCreated'),
          confirmText: 'Continue',
        })
      );
    }
    if (!isNullOrEmpty(selectedCharge)) {
      dispatch(setShouldFetchCharges(true));
    }
    return JSON.parse(res);
  };
};

export const updateReport = (actionCode, reportId, key, value) => {
  return async (dispatch) => {
    const updateAction = {
      ActionCode: actionCode,
      ReportId: reportId,
      [key]: value,
    };
    const res = await updateReportApi(updateAction);
    if (res !== '{}') {
      dispatch(
        openAlert({
          title: i18n.t('tripExpense:ExpenseMessage.ReportNoUpdated'),
          confirmText: 'Continue',
        })
      );
    }
    const actionType = { ActionCode: expenseActions.GET_REPORTS_AND_EXPENSES };
    dispatch(setExpenseResults(actionType));
  };
};

export const deleteExpenseReport = (params) => {
  return async (dispatch) => {
    const res = await updateReportApi(params);
    if (res !== '{}') {
      dispatch(
        openAlert({
          title: i18n.t('tripExpense:ExpenseMessage.ReportNoUpdated'),
          confirmText: 'Continue',
        })
      );
    }
    const actionType = { ActionCode: expenseActions.GET_REPORTS_AND_EXPENSES };
    dispatch(setExpenseResults(actionType));
  };
};

export const setChargesInUseForExpense = (creditCardList) => {
  return async (dispatch) => {
    await performChargesAction({
      ActionCode: chargeActions.SET_CHARGE_IN_USE_FOR_EXPENSE,
      CreditCardList: creditCardList,
      IsExpense: 0,
    });
    dispatch(setShouldFetchCharges(true));
  };
};

export const updateExpense = async (reportId, expense) => {
  const { ExpenseId, CategoryId } = expense;
  const receipts = expense.receipts;
  const currentSelectedSubCategory = expense[DynamoDBFieldNames.SELECTED_SUBCATEGORY];
  const assembledExpense = assembleExpense(expense, receipts, reportId, currentSelectedSubCategory);
  const params = {
    ActionCode: expenseActions.UPDATE_EXPENSE,
    ExpenseId: ExpenseId,
    // ExpenseCategory is required to update the expense and it must be the expense GUID
    // CategoryId stores the expense GUID in the DynamoDB
    Expense: { ...assembledExpense, ExpenseCategory: CategoryId },
  };
  await updateExpenseApi(params);
};

export const submitExpense = (expense) => {
  return async (dispatch) => {
    const res = await submitReport(expense.ReportId);

    if (!res?.Success) {
      dispatch(
        openAlert({
          title: i18n.t('tripExpense:ExpenseMessage.ReportNoSubmitted'),
          confirmText: 'Continue',
        })
      );
    }

    dispatch(setExpenseResults());
  };
};

export const moveExpense = (originId, destinationId, expenseId) => {
  return async (dispatch) => {
    const res = await moveExpenseApi(originId, expenseId, destinationId);

    if (!res?.Success) {
      dispatch(
        openAlert({
          title: i18n.t('tripExpense:ExpenseMessage.ExpenseNoMoved'),
          confirmText: 'Continue',
        })
      );
    }

    dispatch(setExpenseResults());
  };
};

export const moveExpenseNew = (actionType, ExpenseId, DestReportId) => {
  return async (dispatch) => {
    let params = {};
    try {
      if (isNullOrEmpty(DestReportId)) {
        const newReport = await performExpenseAction({ ActionCode: expenseActions.CREATE });
        params = {
          ActionCode: actionType,
          ExpenseId: ExpenseId,
          ReporIdToMove: JSON.parse(newReport).ReportId,
        };
      } else {
        params = {
          ActionCode: actionType,
          ExpenseId: ExpenseId,
          ReporIdToMove: DestReportId,
        };
      }
      await performExpenseAction(params);
      dispatch(setExpenseResults({ ActionCode: expenseActions.GET_REPORTS_AND_EXPENSES }));
    } catch (error) {
      logException(error, {
        metaData: {
          error,
          message: i18n.t('tripExpense:ExpenseMessage.ExpenseNoMoved'),
        },
      });
    }
  };
};

export const deleteExpense = (reportId, expenseId) => {
  return async (dispatch) => {
    const res = await deleteExpenseApi(reportId, expenseId);

    if (!res?.Success) {
      dispatch(
        openAlert({
          title: i18n.t('tripExpense:ExpenseMessage.ExpenseNoDeleted'),
          confirmText: 'Continue',
        })
      );
    }

    dispatch(setExpenseResults());
  };
};

export const updateLastPosition = (lastPosition) => {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_LAST_POSITION,
      lastPosition,
    });
  };
};

export const updateDistance = (position) => {
  return async (dispatch, getState) => {
    const lastPosition = selectExpenseMiliageLastPosition(getState());
    const distance = selectExpenseMiliage(getState());
    if (lastPosition === null) {
      dispatch({
        type: UPDATE_LAST_POSITION,
        lastPosition: position,
      });
    } else {
      if (
        !isNaN(position.latitude) &&
        !isNaN(position.longitude) &&
        !isNaN(lastPosition.latitude) &&
        !isNaN(lastPosition.longitude)
      ) {
        const newDistance = distance + haversine(lastPosition, position, { unit: 'mile' });
        if (!isNaN(newDistance)) {
          dispatch({
            type: UPDATE_MILIAGE,
            miliage: Number(newDistance.toFixed(2)),
            lastPosition: position,
          });
        }
      }
    }
  };
};

export const resetMiliage = () => {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_MILIAGE,
      miliage: 0,
      lastPosition: null,
    });
  };
};

export const getExpenseCategories = () => {
  return async (dispatch) => {
    // uncomment the next line when the API returns the categories with the new format
    // const expenseCategories = await getExpenseCategoriesApi();
    const expenseCategories = ExpenseCategories;

    dispatch({
      type: SET_EXPENSE_CATEGORIES,
      expenseCategories: expenseCategories.filter(
        (category) => category.CategoryId !== DEFAULT_CATEGORY.CategoryId
      ),
    });
  };
};

export const getExpenseDistanceRate = () => {
  return async (dispatch) => {
    const distanceRates = await getExpenseDistanceRatesApi();

    dispatch({
      type: SET_EXPENSE_DISTANCE_RATES,
      distanceRates,
    });
  };
};

export const getExpenseAttendees = () => {
  return async (dispatch) => {
    const attendees = await getAttendeesApi();

    dispatch({
      type: SET_EXPENSE_ATTENDEES,
      attendees,
    });
  };
};

export const getCurrenciesList = () => {
  return async (dispatch) => {
    const currencies = await getCurrencyListApi();

    dispatch({
      type: SET_CURRENCY_LIST,
      currencies,
    });
  };
};

export const getCreditCardsList = () => {
  return async (dispatch) => {
    const creditCards = await getCreditCardsListApi();
    dispatch({
      type: SET_CREDIT_CARDS_LIST,
      creditCards,
    });
  };
};

export const setCurrentReportIdForUpdate = (reportId) => {
  return async (dispatch) => {
    dispatch({ type: SET_CURRENT_REPORT_ID_FOR_UPDATE, reportId });
  };
};

export const setCurrentSelectedCategoryForManualTab = (subCategory) => {
  return async (dispatch) => {
    dispatch({ type: SET_CURRENT_SELECTED_SUBCATEGORY_FOR_MANUAL_TAB, subCategory });
  };
};

export const setReceiptsOfCurrentUser = (receipts) => {
  return async (dispatch) => {
    dispatch({ type: SET_CURRENT_RECEIPTS_OF_USER, receipts });
  };
};

export const setReceiptsState = (receipts, actionType, subTab) => {
  return async (dispatch) => {
    dispatch({ type: SET_RECEIPTS_STATE, receipts, actionType, subTab });
  };
};

export const setCurrentExpenseReportCardModalItem = (item) => {
  return async (dispatch) => {
    dispatch({ type: SET_CURRENT_EXPENSE_REPORT_CARD_MODAL_ITEM, item });
  };
};

export const setLostReceipt = (actionType, subTab) => {
  return async (dispatch) => {
    dispatch({ type: SET_LOST_RECEIPT, actionType, subTab });
  };
};

export const setExpenseValue = (payload) => {
  return async (dispatch) => {
    dispatch({ type: SET_EXPENSE_VALUE, payload });
  };
};

export const initiateExpenseValues = (payload) => {
  return async (dispatch) => {
    dispatch({ type: INITIATE_EXPENSE_VALUES, payload });
  };
};

export const setMultipleExpenseValues = (payload) => {
  return async (dispatch) => {
    dispatch({ type: SET_MULTIPLE_EXPENSE_VALUES, payload });
  };
};

export const setShouldFetchCharges = (shouldFetchCharges) => {
  return async (dispatch) => {
    dispatch({ type: SET_SHOULD_FETCH_CHARGES, shouldFetchCharges });
  };
};

export const setSelectedChargesQueue = (charges) => {
  return async (dispatch) => {
    dispatch({ type: SET_SELECTED_CHARGES_QUEUE, charges });
  };
};

export const clearSelectedChargesQueue = () => {
  return async (dispatch) => {
    dispatch({ type: CLEAR_SELECTED_CHARGES_QUEUE });
  };
};
